import Vue from 'vue'
import router from '@/router/index'
import { getField, updateField } from 'vuex-map-fields'

import CartModel from '@/models/CartModel'
import CartItemModel from '@/models/CartItemModel'
import OrderModel from '@/models/OrderModel'
import SectionModel from '@/models/SectionModel'

// import stall from '@/utils/stall'

import TestItems from '@/store/TestItems'
import {
  FAMILY_DISCOUNT_ITEM_NAME,
  ALL_APP_ITEM_NAMES,
} from '@/constants/AppItemNames'

import Mtxs from '@/constants/Mutations'
import { CustomerRoutes } from '@/constants/RouteNames'

import _get from 'lodash/get'
import _sortBy from 'lodash/sortBy'
import _keyBy from 'lodash/keyBy'
import _pickBy from 'lodash/pickBy'
import _includes from 'lodash/includes'

const testing = false

export const state = {
  availability: [],
  selectedAvailability: null,
  deliveryAddress: '',
  searchDate: null,
  cartItems: testing ? TestItems : {},
  oversoldItems: {},
  placingOrder: false,
  numOrderAttempts: 0,
  hasOrderError: false,
  didOrderTimeout: false,
  isCartOnScreen: false,
  isMenuFormValid: false,
  hightlightedItemId: null,
  emphasizeCartErrors: false,
  showOrderConfSnackbar: false,
  showDialogOversold: false,
  showDialogCartDatePicker: false,
  visibleMenuSections: [],
  isScrollObservationAllowed: true,
  hasAcknowledgedPayment: false,
  orderId: null,
  cart: null,
}
export const mutations = {
  [Mtxs.SET_CART](state, cart) {
    state.cart = cart
  },
  [Mtxs.SET_FULFILLMENT_SLOT](state, fulfillment_slot) {
    CartModel.update({ where: state.cart.id, data: { fulfillment_slot } })
  },
  [Mtxs.SET_SELECTED_AVAILABILITY](state, availability_id) {
    CartModel.update({ where: state.cart.id, data: { availability_id } })
  },
  [Mtxs.SET_FULFILLMENT_TYPE](state, fulfillment_type) {
    CartModel.update({ where: state.cart.id, data: { fulfillment_type } })
  },
  [Mtxs.SET_DELIVERY_FEE](state, delivery_fee) {
    CartModel.update({ where: state.cart.id, data: { delivery_fee } })
  },
  [Mtxs.SET_FAMILY_DISCOUNT](state, family_discount) {
    CartModel.update({ where: state.cart.id, data: { family_discount } })
  },
  [Mtxs.SET_DELIVERY_ADDRESS](state, address) {
    state.deliveryAddress = address
  },
  [Mtxs.SET_SPECIAL_INSTRUCTIONS](state, special_instructions) {
    CartModel.update({ where: state.cart.id, data: { special_instructions } })
  },
  [Mtxs.SET_IS_GIFT](state, is_gift) {
    CartModel.update({ where: state.cart.id, data: { is_gift } })
  },
  [Mtxs.SET_GIFTEE_DETAILS](state, giftee_details) {
    CartModel.update({ where: state.cart.id, data: { giftee_details } })
  },
  [Mtxs.SET_TIP](state, tipAmount) {
    CartModel.update({ where: state.cart.id, data: { tip: tipAmount } })
  },
  [Mtxs.REMOVE_TIP](state) {
    CartModel.update({ where: state.cart.id, data: { tip: undefined } })
  },

  [Mtxs.SET_CART_ITEMS](state, items) {
    state.cartItems = items
  },
  [Mtxs.ADD_CART_ITEM](state, item) {
    CartItemModel.insertOrUpdate({
      data: {
        id: item.id,
        item_id: item.id,
        quantity: Number(item.quantity),
        cart_id: state.cart.id,
      },
    })
  },
  [Mtxs.REMOVE_CART_ITEM](state, item) {
    CartItemModel.delete(item.id)
  },
  [Mtxs.UPDATE_CART_ITEM](state, item) {
    Vue.set(state.cartItems, item.id, item)
  },
  [Mtxs.SET_CART_ON_SCREEN](state, isOnScreen) {
    state.isCartOnScreen = isOnScreen
  },
  [Mtxs.SET_ORDER_CONF_SNACKBAR](state, visibility) {
    state.showOrderConfSnackbar = visibility
  },
  [Mtxs.SET_OVERSOLD_DIALOG](state, visibility) {
    state.showDialogOversold = visibility
  },
  [Mtxs.SET_ORDER_ID](state, id) {
    state.orderId = id
  },
  [Mtxs.SET_PLACING_ORDER](state, isWorking) {
    state.placingOrder = isWorking
  },
  [Mtxs.SET_ORDER_ERROR](state, hasError) {
    state.hasOrderError = hasError
  },
  [Mtxs.SET_ORDER_TIMEOUT](state, didTimeout) {
    state.didOrderTimeout = didTimeout
  },
  [Mtxs.SET_HIGHLIGHTED_ITEM_ID](state, itemId) {
    state.hightlightedItemId = itemId
  },

  [Mtxs.SET_NUM_ORDER_ATTEMPTS](state, num) {
    state.numOrderAttempts = num
  }, // SET_NUM_ORDER_ATTEMPTS

  [Mtxs.SET_CART_ERROR_EMPHASIS](state, emphasis) {
    state.emphasizeCartErrors = emphasis
  }, // SET_CART_ERROR_EMPHASIS

  updateField,
}
export const getters = {
  getTheCart: (state) => {
    if (!state.cart) return {}
    return CartModel.query()
      .whereId(state.cart.id)
      .with(['cartItems.item', 'availability'])
      .first()
  },

  getTipFromCart: (state, getters) => _get(getters.getTheCart, 'tip'),
  hasTip: (state, getters) => typeof getters.getTipFromCart !== 'undefined',

  getSalesTaxFromCart: (state) => _get(state, 'cart.sales_tax', 0),

  getFamilyDiscountAmount: (state, getters) =>
    _get(getters.getTheCart, 'family_discount_amount'),

  getAppliedDeliveryFee: (state, getters) =>
    getters.isDelivery ? _get(state, 'cart.delivery_fee', 0) : 0,

  getAppliedSalesTax: (state, getters) => {
    // If sales tax is a fractional cent over 1, round
    // otherwise set to 0
    return getters.getTheCart.sales_tax_amount > 1
      ? Math.round(getters.getTheCart.sales_tax_amount)
      : 0
  },

  getServiceFee: (state, getters) => _get(getters.getTheCart, 'service_fee'),

  getServiceFeeFormatted: (state, getters) =>
    _get(getters.getTheCart, 'service_fee_formatted', 0),

  // Cart

  cartItems: (state) => {
    let items = _keyBy(
      _sortBy(state.cartItems, (i) => ALL_APP_ITEM_NAMES.indexOf(i.name)),
      'id'
    )
    // remove empty family discount or tax
    items = _pickBy(
      items,
      (i) => !(i.name === FAMILY_DISCOUNT_ITEM_NAME && i.price == 0)
      // Is Empty
    )
    return items
  },

  hasItemsInCart: (_, getters) => Object.keys(getters.cartItems).length,

  realCartItems: (_, __, ___, rootGetters) => {
    let realItemIds = rootGetters['itemAndSection/getRealItems'].map(
      (i) => i.id
    )
    return CartItemModel.query()
      .whereIdIn(realItemIds)
      .get()
  },

  hasRealItemsInCart: (_, getters) => Object.keys(getters.realCartItems).length,

  appCartItems: (state) =>
    _pickBy(state.cartItems, (i) => _includes(ALL_APP_ITEM_NAMES, i.name)),

  appItemsTotal: (_, getters) => {
    let items = getters.appCartItems
    return Object.keys(items)
      .map((key) => Number(_get(items[key], 'price', 0)) * items[key].quantity)
      .reduce((curr, sum) => {
        return sum + curr
      }, 0)
  }, // appItemsTotal

  itemsToCreate: (state, getters) => {
    return Object.entries(getters.cartItems).map((array) => {
      let item = array[1]
      let price = _get(item, 'price', 0).toFixed(2)
      return {
        Total: `$${(price * item.quantity).toFixed(2)}`,
        Name: item.name,
        Price: `$${price}`,
        Unit: item.unit,
        quantity: item.quantity,
      }
    })
  }, // itemsToCreate

  getOrderData: (_, getters) => {
    const line_items = getters.getTheCart.cartItems.map(
      (cartItem) => cartItem.data_for_order
    )
    return {
      completed: false,
      paid: false,
      fulfillment_slot: getters.getFulfillmentSlot,
      fulfillment_type: getters.getFulfillmentType,
      availability_id: getters.getSelectedAvailability,
      special_instructions: getters.getSpecialInstructions,
      is_gift: getters.getIsGift,
      giftee_details: getters.getGifteeDetails,
      delivery_address: getters.getDeliveryAddress,
      delivery_fee: getters.getAppliedDeliveryFee,
      family_discount: getters.getTheCart.family_discount_amount ?? 0,
      line_items,
      user_id: getters.getTheCart.user_id,
      tax: getters.getAppliedSalesTax,
      tip: getters.getTipFromCart,
    }
  },

  getCartItemByName: () => (itemName) =>
    CartItemModel.query()
      .with('item', (query) => {
        query.where('name', itemName)
      })
      .get(), // getCartItemByName

  getCartItemById: () => (id) => CartItemModel.find(id), // getCartItemById

  getOversoldItemById: () => (id) =>
    CartItemModel.query()
      .where('oversold', true)
      .where('id', id)
      .get(), // getOversoldItemById

  getOversoldItems: () =>
    CartItemModel.query()
      .with('item')
      .where('oversold', true)
      .get(),

  cartSubtotal: (_, getters) => _get(getters.getTheCart, 'subtotal_usd', 0), // cartSubtotal

  cartTotal: (state, getters) => _get(getters.getTheCart, 'total_usd', 0), // cartTotal
  cartHasTotal: (state, getters) => getters.cartTotal > 0, // cartHasTotal

  showScrollToCart: (state, getters) =>
    !state.isCartOnScreen && getters.hasRealItemsInCart, // showScrollToCart

  getHighlightedItemId: (state) => state.hightlightedItemId,

  // Fulfillment

  getSelectedAvailability: (_, getters) => getters.getTheCart.availability_id,
  getFulfillmentType: (_, getters) => getters.getTheCart.fulfillment_type,
  isDelivery: (_, getters) => getters.getFulfillmentType === 'Delivery',
  getFulfillmentSlot: (state, getters) =>
    _get(getters.getTheCart, 'fulfillment_slot'),
  getDeliveryAddress: (state, getters, _, rootGetters) => {
    if (state.deliveryAddress.length) {
      return state.deliveryAddress
    }
    if (getters.getTheCart.delivery_address) {
      return getters.getTheCart.delivery_address
    }
    return rootGetters['auth/getAuthUserAddress']
  },
  getSpecialInstructions: (_, getters) =>
    getters.getTheCart.special_instructions,
  getIsGift: (_, getters) => _get(getters.getTheCart, 'is_gift', false),
  getGifteeDetails: (_, getters) =>
    _get(getters.getTheCart, 'giftee_details', ''),
  getOrderId: (state) => state.orderId,

  // App states and snackbars

  getShowOrderConfSnackbar: (state) => state.showOrderConfSnackbar,
  getPlacingOrder: (state) => state.placingOrder,
  getNumOrderAttempts: (state) => state.numOrderAttempts,
  getHasOrderError: (state) => state.hasOrderError,
  getDidOrderTimeout: (state) => state.didOrderTimeout,
  getEmphasizeCartErrors: (state) => state.emphasizeCartErrors,
  getField,
}
export const actions = {
  async initCart({ commit, rootGetters }, is_custom) {
    let shop = rootGetters['shop/getShop']

    let { cart: cartEntities } = await CartModel.insert({
      data: {
        sales_tax: shop.sales_tax,
        delivery_fee: shop.delivery_fee,
        family_discount: rootGetters['auth/getAuthUserFamilyDiscount'],
        shop_id: shop.id,
        is_custom,
        user_id: rootGetters['auth/getAuthUserId'],
      },
    })

    commit(Mtxs.SET_CART, cartEntities[0])
    return cartEntities[0]
  },

  setFulfillmentSlot({ commit }, slot) {
    commit(Mtxs.SET_FULFILLMENT_SLOT, slot)
  },

  setSelectedAvailability({ commit }, availabilityId) {
    commit(Mtxs.SET_SELECTED_AVAILABILITY, availabilityId)
  },
  setFulfillmentType({ commit }, type) {
    commit(Mtxs.SET_FULFILLMENT_TYPE, type)
  },
  setDeliveryFee({ commit }, fee) {
    commit(Mtxs.SET_DELIVERY_FEE, fee)
  },
  setDeliveryAddress({ commit }, address) {
    commit(Mtxs.SET_DELIVERY_ADDRESS, address)
  },
  setSpecialInstructions({ commit }, special_instructions) {
    commit(Mtxs.SET_SPECIAL_INSTRUCTIONS, special_instructions.trim())
  },
  setIsGiftVal({ commit }, isGift) {
    commit(Mtxs.SET_IS_GIFT, isGift)
  },
  setGifteeDetails({ commit }, giftee_details) {
    commit(Mtxs.SET_GIFTEE_DETAILS, giftee_details)
  },
  setFamilyDiscount({ commit }, discount) {
    commit(Mtxs.SET_FAMILY_DISCOUNT, discount)
  },
  setCartItems({ commit }, items) {
    commit(Mtxs.SET_CART_ITEMS, items)
  },
  addCartItem({ commit }, item) {
    commit(Mtxs.ADD_CART_ITEM, item)
    if (Vue.$gtag) {
      const sectionName = SectionModel.find(item.section_id).name || 'Section'

      Vue.$gtag.event('add_to_cart', {
        currency: 'USD',
        items: [
          {
            id: item.id,
            name: item.name,
            category: `Menu/${sectionName}`,
            list_name: sectionName,
            list_position: item.listPosition,
            price: Number(item.price),
            quantity: Number(item.quantity),
          },
        ],
        value: Number(item.price),
      })
    }
  },
  removeCartItem(ctx, item) {
    CartItemModel.delete(item.id)
  },
  updateCartItem(ctx, item) {
    CartItemModel.update({ where: item.id, data: item })
  },

  setCartOnScreen({ commit }, isOnScreen) {
    commit(Mtxs.SET_CART_ON_SCREEN, isOnScreen)
  },
  initTip({ commit, rootGetters }) {
    const defaultTip = Number(rootGetters['shop/getTipDefault'])
    commit(Mtxs.SET_TIP, defaultTip)
  },
  updateTip({ commit }, tipAmount) {
    commit(Mtxs.SET_TIP, Number(tipAmount * 100))
  },
  removeTip({ commit }) {
    commit(Mtxs.REMOVE_TIP)
  }, // removeTip

  setOrderId({ commit }, orderId) {
    commit(Mtxs.SET_ORDER_ID, orderId)
  }, // setOrderId

  // Orders & Items

  async createOrder({ dispatch, getters, rootGetters }) {
    dispatch('setOrderError', false)
    dispatch('setPlacingOrder', true)

    try {
      // fetch items/menu again to compare stock vs requested quantities
      await dispatch('shop/fetchMenu', null, { root: true })
      let products = await getters.getTheCart.products

      let oversoldCartItems = await products.filter(
        (cartItem) =>
          !cartItem.item.unlimited && cartItem.quantity > cartItem.item.stock
      )
      if (oversoldCartItems.length) {
        dispatch('showDialogOversold')
        await dispatch('updateOversoldItemQuantities', oversoldCartItems)
        return false
      }
      // if everything's good, send it
      let { response: newOrderResponse } = await OrderModel.createShopOrder(
        getters.getOrderData
      )
      let newOrder = newOrderResponse.data.data
      if (Vue.$gtag) {
        Vue.$gtag.purchase({
          transaction_id: newOrder.id,
          affiliation: rootGetters['shop/getShopTitle'],
          value: Number(getters.cartTotal),
        })
      }

      dispatch('setOrderConfSnackbar', true)
      router.push({
        name: CustomerRoutes.ORDER_VIEW,
        params: { orderId: newOrder.id },
      })
      dispatch('resetCart')
    } catch (error) {
      dispatch('setOrderError', true)
    } finally {
      dispatch('setPlacingOrder', false)
    }
  },

  async createOrderForAdmin({ dispatch, getters }) {
    dispatch('setOrderError', false)
    dispatch('setPlacingOrder', true)

    try {
      // fetch items/menu again to compare stock vs requested quantities
      // if everything's good, send it
      await OrderModel.createShopOrder(getters.getOrderData)
      dispatch('setOrderConfSnackbar', true)
      dispatch('resetCart')
    } catch (error) {
      dispatch('setOrderError', true)
    } finally {
      dispatch('setPlacingOrder', false)
    }
  },

  resetCart({ commit }) {
    CartItemModel.deleteAll()
    commit('updateField', { path: 'hasAcknowledgedPayment', value: false })
    commit('updateField', { path: 'selectedAvailability', value: null })
  },

  setOrderFailed({ dispatch }) {
    dispatch('setOrderError', true)
    dispatch('setPlacingOrder', false)
  },

  setOrderTimeout({ dispatch, commit }, didTimeout) {
    commit(Mtxs.SET_ORDER_TIMEOUT, didTimeout)
    dispatch('setPlacingOrder', false)

    if (didTimeout) {
      if (Vue.$gtag) {
        Vue.$gtag.event('order_timeout')
      }
    }
  }, // setOrderTimeout

  setEmphasizeCartErrors({ commit }, val) {
    commit(Mtxs.SET_CART_ERROR_EMPHASIS, val)
  }, // setEmphasizeCartErrors

  async updateOversoldItemQuantities({ dispatch }, oversoldCartItems) {
    if (!oversoldCartItems || !oversoldCartItems.length) return true

    await oversoldCartItems.forEach(async (cartItem) => {
      let updatedItem = {
        ...cartItem,
        oversold: true,
        quantity: cartItem.item.stock,
      }
      await dispatch('updateCartItem', updatedItem)
    })
  }, // updateOversoldItemQuantities

  setOrderConfSnackbar({ commit }, visibility) {
    commit(Mtxs.SET_ORDER_CONF_SNACKBAR, visibility)
  },

  showDialogOversold({ commit }) {
    commit(Mtxs.SET_OVERSOLD_DIALOG, true)
    commit(Mtxs.SET_PLACING_ORDER, false)
    if (Vue.$gtag) {
      Vue.$gtag.event('cart_oversold_msg')
    }
  },

  hideDialogOversold({ commit }) {
    commit(Mtxs.SET_OVERSOLD_DIALOG, false)
  },
  setPlacingOrder({ commit }, isWorking) {
    commit(Mtxs.SET_PLACING_ORDER, isWorking)
  },
  setOrderError({ commit }, hasError) {
    commit(Mtxs.SET_ORDER_ERROR, hasError)

    if (hasError) {
      if (Vue.$gtag) {
        Vue.$gtag.event('cart_error_msg')
      }
    }
  }, // setOrderError
  highlightItem({ commit }, itemId) {
    commit(Mtxs.SET_HIGHLIGHTED_ITEM_ID, itemId)
    setTimeout(() => {
      commit(Mtxs.SET_HIGHLIGHTED_ITEM_ID, null)
    }, 3000)
  },
} // actions
